import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    catchError,
    defer,
    forkJoin,
    from,
    map,
    mergeMap,
    Observable,
    of,
    retry,
    switchMap,
    throwError,
    timer,
    toArray
} from 'rxjs';
import { LanguageCode } from '@shs-digi-care-monorepo/interfaces';
import { TranslationTree } from '../models/translation-editor.model';
import { TranslationEditorOperations } from '../operations/translation-editor.operations';

export type SignedUrlResponse = {
    url: string;
    expiresAt: string;
};

export type TranslateInput = {
    from: LanguageCode;
    to: LanguageCode;
    key: string;
    value: string;
};

const BATCH_SIZE = 20;
const PARALLEL_REQUESTS = 10;
const REQUEST_DURATION_MS = 600;
const MAX_RETRIES = 3;
const MOCK_FAILURE_RATE = 0.15;

/**
 * Service that loads translation JSON files served from `/assets/i18n/<lang>.json`,
 * flattens / unflattens nested keys, and triggers downloads of edited files.
 *
 * Browser sandboxing prevents writing back to disk, so persistence is implemented
 * as a download – the user replaces the source file in the repository.
 */
@Injectable({ providedIn: 'root' })
export class TranslationEditorService {
    private readonly http = inject(HttpClient);

    /**
     *
     * @param input
     * @returns
     */
    public translate(input: TranslateInput[]): Observable<string[]> {
        if (input.length === 0) {
            return of([]);
        }

        const batches: TranslateInput[][] = [];
        for (let i = 0; i < input.length; i += BATCH_SIZE) {
            batches.push(input.slice(i, i + BATCH_SIZE));
        }

        return from(batches.map((batch, index) => ({ batch, index }))).pipe(
            mergeMap(
                ({ batch, index }) => this.translateBatch(batch).pipe(map((result) => ({ index, result }))),
                PARALLEL_REQUESTS
            ),
            toArray(),
            map((entries) => {
                const ordered: string[][] = new Array(batches.length);
                for (const { index, result } of entries) {
                    ordered[index] = result;
                }

                // return ordered.flat();
                return ([] as string[]).concat(...ordered);
            })
        );
    }

    /**
     *
     * @returns
     */
    public availableLanguages(): Observable<string[]> {
        return of([
            'cs',
            'da',
            'de',
            'en-gb',
            'en-us',
            'et',
            'fi',
            'fr-be',
            'lt',
            'lv',
            'nl-be',
            'nl',
            'no',
            'pl',
            'sv',
            'tr'
        ]);
    }

    private readonly basePath = '/assets/i18n/';

    /**
     * Load a translation file via a two-step flow:
     *   1. Ask the backend for a short-lived signed download URL.
     *   2. Fetch the JSON from that URL (typically S3).
     *
     * Today both steps are mocked: the "signed URL" simply points at the
     * static asset under `/assets/i18n/`. Swapping in the real endpoint
     * later only requires changing `requestDownloadUrl`.
     */
    public load(language: string): Observable<TranslationTree> {
        return this.requestDownloadUrl(language).pipe(
            switchMap(({ url }) => this.http.get<TranslationTree>(url)),
            catchError(() => of({} as TranslationTree))
        );
    }

    public loadAll(languages: string[]): Observable<Record<string, TranslationTree>> {
        if (languages.length === 0) {
            return of({});
        }
        return forkJoin(
            languages.reduce<Record<string, Observable<TranslationTree>>>((acc, lang) => {
                acc[lang] = this.load(lang);
                return acc;
            }, {})
        );
    }

    /**
     * Persist a translation file via a two-step flow:
     *   1. Ask the backend for a short-lived signed upload URL.
     *   2. PUT the JSON payload to that URL (typically S3).
     *
     * Mocked for now – the upload step logs the payload size and resolves.
     */
    public save(language: string, tree: TranslationTree): Observable<void> {
        const sorted = TranslationEditorOperations.sortTree(tree);
        const json = JSON.stringify(sorted, null, 4) + '\n';
        const blob = new Blob([json], { type: 'application/json' });

        return this.requestUploadUrl(language).pipe(
            switchMap(({ url }) => this.uploadToSignedUrl(url, blob)),
            map(() => undefined)
        );
    }

    /**
     * Persist multiple translation files in a single backend call. Errors are
     * reported atomically – either the whole batch succeeds or the request fails.
     *
     * Mocked for now – delegates to {@link save} per language under the hood.
     * When the real endpoint exists, replace the body with a single HTTP call
     * carrying the full payload.
     */
    public saveAll(trees: Record<string, TranslationTree>): Observable<void> {
        const entries = Object.entries(trees);
        if (entries.length === 0) {
            return of(undefined);
        }
        // TODO: replace with real bulk endpoint, e.g.
        // return this.http.put<void>('/api/translations/bulk', payload);
        return forkJoin(entries.map(([language, tree]) => this.save(language, tree))).pipe(map(() => undefined));
    }

    private requestDownloadUrl(language: string): Observable<SignedUrlResponse> {
        // TODO: replace with real call, e.g.
        // return this.http.get<SignedUrlResponse>(`/api/translations/${language}/download-url`);
        return of({
            url: `${this.basePath}${language}.json`,
            expiresAt: new Date(Date.now() + 60_000).toISOString()
        });
    }

    private requestUploadUrl(language: string): Observable<SignedUrlResponse> {
        // TODO: replace with real call, e.g.
        // return this.http.post<SignedUrlResponse>(`/api/translations/${language}/upload-url`, {});
        return of({
            url: `mock://upload/${language}.json`,
            expiresAt: new Date(Date.now() + 60_000).toISOString()
        });
    }

    private uploadToSignedUrl(url: string, blob: Blob): Observable<void> {
        // TODO: replace with real PUT once the backend issues S3 signed URLs:
        // return this.http.put<void>(url, blob, { headers: { 'Content-Type': 'application/json' } });
        return defer(() => {
            console.info('[TranslationEditorService] mock upload', { url, size: blob.size });
            return of(undefined);
        });
    }

    private translateBatch(batch: TranslateInput[]): Observable<string[]> {
        return defer(() =>
            timer(REQUEST_DURATION_MS).pipe(
                mergeMap(() =>
                    Math.random() < MOCK_FAILURE_RATE
                        ? throwError(() => new Error('Mock translate request failed'))
                        : of(batch.map((x) => `[${x.to}]: ${x.value}`))
                )
            )
        ).pipe(retry({ count: MAX_RETRIES, delay: REQUEST_DURATION_MS }));
    }
}
